/*
 * Beta version of the main app for layout experiments.
 * Same behavior as App; use AppBeta.scss to try layout changes.
 * Available at: /beta
 */

import React from 'react'
import './AppBeta.scss'
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography, Box } from '@material-ui/core'
import BubbleChart from './components/BubbleChart/BubbleChart'
import { Types } from './components/BubbleChart/types'

function AppBeta() {
  const bubbleChartRef = React.useRef<any>(null)

  const [data, setData] = React.useState<Types.Data[]>([])
  const [loading, setLoading] = React.useState<boolean>(true)
  const [generatedAt, setGeneratedAt] = React.useState<string | null>(null)
  const [selectedKeyword, setSelectedKeyword] = React.useState<Types.Data | null>(null)
  const [dialogOpen, setDialogOpen] = React.useState<boolean>(false)

  const loadKeywords = React.useCallback(() => {
    fetch('/keywords.json')
      .then((response) => response.json())
      .then((jsonResponse: { generatedAt?: string; keywords?: Types.Data[] }) => {
        const keywords = jsonResponse.keywords || (Array.isArray(jsonResponse) ? jsonResponse : [])
        setData(keywords)
        if (jsonResponse.generatedAt) {
          setGeneratedAt(jsonResponse.generatedAt)
        }
        setLoading(false)
      })
      .catch((error) => {
        console.error('Error loading keywords.json:', error)
        setLoading(false)
      })
  }, [])

  React.useEffect(() => {
    loadKeywords()
  }, [loadKeywords])

  React.useEffect(() => {
    const intervalId = setInterval(() => loadKeywords(), 60 * 1000)
    return () => clearInterval(intervalId)
  }, [loadKeywords])

  const formatDateTime = (isoString: string | null): string => {
    if (!isoString) return ''
    try {
      let utcString = isoString.trim()
      if (!utcString.endsWith('Z') && !utcString.match(/[+-]\d{2}:\d{2}$/)) {
        utcString = utcString + 'Z'
      }
      const date = new Date(utcString)
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'America/Sao_Paulo'
      })
    } catch {
      return isoString
    }
  }

  const selectedKeyHandler = (key: string) => {
    const keyword = data.find(item => item.name === key)
    if (keyword) {
      setSelectedKeyword(keyword)
      setDialogOpen(true)
    }
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setSelectedKeyword(null)
  }

  if (loading) {
    return (
      <div className="AppBeta">
        <div className="AppBeta-container">
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <p>Loading data...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="AppBeta">
      <div className="AppBeta-container">
        <div className="AppBeta-bubble-container">
          <BubbleChart
            ref={bubbleChartRef}
            bubblesData={data}
            width={1200}
            height={900}
            backgroundColor="#87ceeb"
            minValue={1}
            maxValue={150}
            selectedCircle={selectedKeyHandler}
          />
          {generatedAt && (
            <div className="AppBeta-timestamp">
              Generated: {formatDateTime(generatedAt)}
            </div>
          )}
        </div>
      </div>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '12px',
            padding: '8px'
          }
        }}
      >
        <DialogTitle>
          <Typography variant="h5" component="div" style={{ fontWeight: 'bold' }}>
            {selectedKeyword?.name || 'News'}
          </Typography>
        </DialogTitle>
        <DialogContent dividers style={{ maxHeight: '70vh', overflow: 'auto' }}>
          {selectedKeyword?.news && selectedKeyword.news.length > 0 ? (
            <Box>
              {selectedKeyword.news.map((newsItem, index) => (
                <Box
                  key={index}
                  style={{
                    marginBottom: index < selectedKeyword.news!.length - 1 ? '10px' : '0',
                    paddingBottom: index < selectedKeyword.news!.length - 1 ? '10px' : '0',
                    borderBottom: index < selectedKeyword.news!.length - 1 ? '1px solid #e0e0e0' : 'none'
                  }}
                >
                  <Typography variant="body2" style={{ lineHeight: '1.4', whiteSpace: 'pre-wrap', fontSize: '0.875rem' }}>
                    {newsItem}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary" style={{ fontSize: '0.875rem' }}>
              No news available for this keyword.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary" variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default AppBeta
